// blfRows — pure row-list logic for the Users -> BLF tab (#1231).
//
// The tab shows a minimum of BLF_MIN_ROWS rows. The old implementation padded
// ONLY the rendered list (display-time) while "+ Add more BLF's" appended to
// the UNPADDED state array — so with fewer than 10 real rows a click grew the
// state from e.g. 0 -> 1 but the padded render stayed at exactly 10 rows: the
// button visibly did nothing. Padding is now materialized into state
// (padBlfRows) so state === display, and growing appends to the padded list
// (growBlfRows). This also kills a second latent bug: editing a display-only
// padded row wrote past the end of the state array, leaving sparse holes.
//
// BLF_MAX_KEYS: the API (PUT /api/users-extended/:uuid/blf) accepts any number
// of rows — there is no server-side cap. 128 comfortably covers the largest
// Grandstream layouts we provision (GRP2615: 40 VPKs, +40 per GBX20 module)
// and the current GRP2615 ~40-slot offset workaround (#1230).

export const BLF_MIN_ROWS = 10;
export const BLF_MAX_KEYS = 128;

export type BlfRow = { target_type?: string; target?: string; label?: string; [k: string]: any };

export function blankBlfRow(): BlfRow {
  return { target_type: 'blank', target: '', label: '' };
}

export function isBlankBlfRow(r: BlfRow | null | undefined): boolean {
  if (!r) return true;
  const type = String(r.target_type || '').toLowerCase().trim();
  return (type === '' || type === 'blank') && !r.target && !r.label;
}

// Pad to at least `min` rows with fresh blank rows (distinct objects, so an
// edit to one padded row can never bleed into its siblings).
export function padBlfRows(rows: BlfRow[] | null | undefined, min: number = BLF_MIN_ROWS): BlfRow[] {
  const base = Array.isArray(rows) ? [...rows] : [];
  while (base.length < min) base.push(blankBlfRow());
  return base;
}

// Append one blank row, hard-capped at `max`. Returns the input untouched at cap.
export function growBlfRows(rows: BlfRow[], max: number = BLF_MAX_KEYS): BlfRow[] {
  if (rows.length >= max) return rows;
  return [...rows, blankBlfRow()];
}

// Wire format for the PUT: assign 1-based key_index by display position, then
// drop TRAILING blank rows only. Blanks in the MIDDLE are positional
// placeholders (they keep later keys on the right physical buttons — the
// provision emitters map by key_index) and must be preserved.
export function serializeBlfRows(rows: BlfRow[]): (BlfRow & { key_index: number })[] {
  const indexed = rows.map((k, i) => ({ ...k, key_index: i + 1 }));
  let end = indexed.length;
  while (end > 0 && isBlankBlfRow(indexed[end - 1])) end--;
  return indexed.slice(0, end);
}
